import React, { useState, useMemo } from 'react';
import { usePerfil } from '../../context/PerfilContext';
import { useAuth } from '../../context/AuthContext';
import { MODULOS, MODULOS_ACTIVOS } from '../../config/modulos';
import { PerfilUsuario } from '../../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Search, ShieldCheck, User as UserIcon, Clock, Info, Sparkles } from 'lucide-react';
import { useConfirmar } from '../../context/ConfirmarContext';
import { rellenarCuentaDemo } from '../../utils/cuentaDemo';
import { calcularPlus } from '../../config/planes';

/**
 * Crea o rehace la cuenta de demo para la revisión de Apple (la del email
 * DEMO_EMAIL de Netlify, nunca otra). Conviene hacerlo justo antes de enviar,
 * para que las fechas de los datos de ejemplo sean recientes.
 */
const CuentaDemo: React.FC = () => {
  const confirmar = useConfirmar();
  const [password, setPassword] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; mensaje: string } | null>(null);

  const rellenar = async () => {
    if (password && password.length < 8) {
      setResultado({ ok: false, mensaje: 'La contraseña tiene que tener 8 caracteres o más.' });
      return;
    }
    if (!(await confirmar({
      titulo: '¿Rellenar la cuenta de demo?',
      mensaje: 'Se borra todo lo que tenga la cuenta de demo y se vuelve a llenar con datos de ' +
        'ejemplo. No toca ninguna otra cuenta.',
      accion: 'Rellenar',
    }))) return;
    setTrabajando(true);
    setResultado(null);
    setResultado(await rellenarCuentaDemo(password));
    setTrabajando(false);
    setPassword('');
  };

  return (
    <section className="mt-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        Cuenta de demo para la revisión de Apple
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Deja la cuenta de demo con Finanzas (cuentas, dos meses de movimientos y un presupuesto) y
        A medias («Escapada a Lisboa» con deudas pendientes y un piso compartido). Hazlo justo antes
        de enviar a revisión. La contraseña solo hace falta la primera vez o para cambiarla.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          autoComplete="new-password" placeholder="Contraseña (opcional)"
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button type="button" onClick={rellenar} disabled={trabajando}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold
                     disabled:opacity-50">
          {trabajando ? 'Rellenando…' : 'Rellenar con datos de ejemplo'}
        </button>
      </div>
      {resultado && (
        <p role="status" className={`text-sm mt-2 ${
          resultado.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
        }`}>
          {resultado.mensaje}
        </p>
      )}
    </section>
  );
};

// La cuenta de demo de la revisión de Apple no cuenta como uso real.
const DEMO_EMAIL = 'demo@myarinn.app';
const DIA = 24 * 60 * 60 * 1000;

/**
 * Cuántas personas distintas han entrado hoy, en 7 y en 30 días. Sale del
 * último acceso de cada perfil, así que no hay histórico: es la foto de ahora.
 * No cuenta al administrador que mira ni a la cuenta de demo.
 */
const Activos: React.FC<{ usuarios: PerfilUsuario[]; yo?: string }> = ({ usuarios, yo }) => {
  const cifras = useMemo(() => {
    const ahora = Date.now();
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const reales = usuarios.filter(u => u.id !== yo && u.email !== DEMO_EMAIL);
    const desde = (t: number) => reales.filter(u => u.ultimoAcceso.getTime() >= t).length;
    return [
      { etiqueta: 'Hoy', valor: desde(hoy.getTime()) },
      { etiqueta: 'Últimos 7 días', valor: desde(ahora - 7 * DIA) },
      { etiqueta: 'Últimos 30 días', valor: desde(ahora - 30 * DIA) },
    ];
  }, [usuarios, yo]);

  return (
    <section aria-label="Usuarios activos" className="mb-4">
      <div className="grid grid-cols-3 gap-2">
        {cifras.map(c => (
          <div key={c.etiqueta}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5">
            <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{c.valor}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">{c.etiqueta}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
        Personas distintas que han abierto la app, sin contarte a ti ni a la cuenta de demo.
      </p>
    </section>
  );
};

const Interruptor: React.FC<{
  activo: boolean; onChange: (v: boolean) => void; etiqueta: string; deshabilitado?: boolean;
}> = ({ activo, onChange, etiqueta, deshabilitado }) => (
  <button type="button" role="switch" aria-checked={activo} aria-label={etiqueta}
    disabled={deshabilitado}
    onClick={() => onChange(!activo)}
    className={`relative w-9 h-5 rounded-full transition shrink-0 ${
      activo ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
    } ${deshabilitado ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
      activo ? 'left-[18px]' : 'left-0.5'
    }`} />
  </button>
);

export const PanelUsuarios: React.FC = () => {
  const {
    usuarios, cargandoUsuarios, pendientesDeActivar, cambiarModulo, cambiarRol, perfil,
    configPlus, regalarPlus,
  } = usePerfil();
  const { user } = useAuth();
  const confirmar = useConfirmar();
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState('');

  const lista = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return usuarios.filter(u =>
      !t || [u.email, u.nombre].some(v => v?.toLowerCase().includes(t)));
  }, [usuarios, busqueda]);

  const conError = async (accion: () => Promise<void>) => {
    setError('');
    try { await accion(); }
    catch (e) {
      console.error(e);
      setError('No se pudo guardar el cambio. Revisa que tu cuenta siga siendo administradora.');
    }
  };

  const alternarModulo = (u: PerfilUsuario, idModulo: string, activo: boolean) =>
    conError(() => cambiarModulo(u.id, idModulo, activo));

  const alternarAdmin = async (u: PerfilUsuario) => {
    const seraAdmin = u.rol !== 'admin';
    if (!seraAdmin && u.id === user?.uid) {
      if (!(await confirmar({
        titulo: '¿Quitarte el rol de administrador?',
        mensaje: 'Perderás el acceso a esta pantalla, y solo podrás recuperarlo desde ' +
          'la consola de Firebase.',
        accion: 'Quitármelo',
      }))) return;
    }
    if (seraAdmin && !(await confirmar({
      titulo: `¿Hacer administrador a «${u.email}»?`,
      mensaje: 'Podrá activar módulos y nombrar a otros administradores.',
      accion: 'Hacer administrador',
      destructiva: false,
    }))) return;

    return conError(() => cambiarRol(u.id, seraAdmin ? 'admin' : 'usuario'));
  };

  if (cargandoUsuarios) {
    return <div className="py-10 text-center text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  const noImplementados = MODULOS.filter(m => !m.disponible);

  return (
    <div>

      <header className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Usuarios</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {usuarios.length} cuenta{usuarios.length === 1 ? '' : 's'}
          {pendientesDeActivar > 0 && (
            <span className="text-amber-600 dark:text-amber-400 font-medium">
              {' '}· {pendientesDeActivar} pendiente{pendientesDeActivar === 1 ? '' : 's'} de activar
            </span>
          )}
        </p>
      </header>

      <Activos usuarios={usuarios} yo={user?.uid} />

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                        text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar por email o nombre…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                     focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {lista.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <UserIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            {usuarios.length === 0
              ? 'Aquí solo aparecen las cuentas que han entrado al menos una vez.'
              : 'Ninguna cuenta coincide con la búsqueda.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {lista.map(u => {
            const esYo = u.id === user?.uid;
            const sinNada = u.rol !== 'admin' && !Object.values(u.modulos ?? {}).some(Boolean);
            return (
              <div key={u.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">

                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="font-bold text-gray-900 dark:text-white truncate flex items-center gap-2">
                      {u.nombre || u.email || 'Sin identificar'}
                      {esYo && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide
                                         text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30
                                         px-2 py-0.5 rounded-full">Tú</span>
                      )}
                      {sinNada && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide
                                         text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30
                                         px-2 py-0.5 rounded-full">Pendiente</span>
                      )}
                    </h2>
                    {u.nombre && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                    )}
                    <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <Clock className="w-3 h-3" />
                      Último acceso: {format(u.ultimoAcceso, "d MMM yyyy, HH:mm", { locale: es })}
                    </p>
                  </div>

                  <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                    <ShieldCheck className={`w-4 h-4 ${
                      u.rol === 'admin' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Admin</span>
                    <Interruptor activo={u.rol === 'admin'} etiqueta={`Administrador: ${u.email}`}
                      onChange={() => alternarAdmin(u)} />
                  </label>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mt-3 pt-3
                                border-t border-gray-100 dark:border-gray-700">
                  {MODULOS_ACTIVOS.map(m => (
                    <label key={m.id}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                                 bg-gray-50 dark:bg-gray-900/40 cursor-pointer">
                      <m.Icon className={`w-4 h-4 shrink-0 ${m.acento.texto}`} />
                      <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">
                        {m.nombre}
                      </span>
                      <Interruptor
                        activo={u.rol === 'admin' || u.modulos?.[m.id] === true}
                        deshabilitado={u.rol === 'admin'}
                        etiqueta={`${m.nombre} para ${u.email}`}
                        onChange={v => alternarModulo(u, m.id, v)} />
                    </label>
                  ))}
                </div>

                {(() => {
                  const plus = calcularPlus(u, configPlus);
                  const pagado = u.planOrigen && u.planOrigen !== 'admin' && plus.esPlus;
                  const estado = ({
                    admin: 'Administrador: sin límites',
                    'sin-limites': 'Sin límites todavía (no se han activado)',
                    pagado: `Pagado (${u.planOrigen}) hasta el ${plus.hasta ? format(plus.hasta, 'd MMM yyyy', { locale: es }) : '—'}`,
                    vitalicio: `De por vida (${u.planOrigen})`,
                    regalado: 'Regalado por un administrador',
                    regalo: `Regalo de bienvenida hasta el ${plus.hasta ? format(plus.hasta, 'd MMM yyyy', { locale: es }) : '—'}`,
                  } as Record<string, string>)[plus.motivo ?? ''] ?? 'Plan gratis';
                  return (
                    <div className="flex items-center gap-2.5 mt-2 px-2.5 py-2 rounded-lg bg-amber-50/60 dark:bg-amber-900/10">
                      <Sparkles className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400" />
                      <span className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300">
                        Plus <span className="text-xs text-gray-500 dark:text-gray-400">· {estado}</span>
                      </span>
                      {u.rol !== 'admin' && !pagado && (
                        <Interruptor activo={u.planOrigen === 'admin' && u.planVitalicio === true}
                          etiqueta={`Regalar Plus a ${u.email}`}
                          onChange={v => conError(() => regalarPlus(u.id, v))} />
                      )}
                    </div>
                  );
                })()}

                {u.rol === 'admin' && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-2">
                    Los administradores entran en todos los módulos; por eso los interruptores
                    están fijos.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CuentaDemo />

      <div className="mt-6 space-y-2 text-[11px] text-gray-500 dark:text-gray-500">
        <p className="flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          Aquí solo salen las cuentas que han entrado al menos una vez desde que existe esta
          pantalla. Para ver o borrar todas las cuentas registradas, Firebase Console →
          Authentication → Users.
        </p>
        {noImplementados.length > 0 && (
          <p className="flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
            {noImplementados.map(m => m.nombre).join(', ')} todavía no {noImplementados.length === 1 ? 'está construido' : 'están construidos'},
            así que no {noImplementados.length === 1 ? 'aparece' : 'aparecen'} en la lista de módulos.
          </p>
        )}
        {perfil && (
          <p className="flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
            Si te quedas sin acceso a esta pantalla, se recupera poniendo
            <code className="mx-1 px-1 rounded bg-gray-100 dark:bg-gray-700">rol: "admin"</code>
            en tu documento de la colección <code className="px-1 rounded bg-gray-100 dark:bg-gray-700">usuarios</code>
            {' '}desde la consola de Firebase.
          </p>
        )}
      </div>
    </div>
  );
};
